import base58 from "bs58";
import { TFunction } from "i18next";
import {
  GetTradeHistory200ResponseTradeHistoryInner,
  SideResponse,
} from "../codegen-api";

/**
 * Why a close row exists, as reported by the backend. Absent on manual closes
 * and on every opening trade — and absent entirely on chains/endpoints that
 * don't emit it, which must keep rendering exactly as before.
 *
 * NOTE: distinct from the `CloseReason` union in `interfaces/history.ts`,
 * which describes position-history rows and is NOT interchangeable with this.
 */
export type TradeExitReason = "take_profit" | "stop_loss" | "liquidation";

const EXIT_REASON_LABELS: Record<
  TradeExitReason,
  { key: string; defaultValue: string }
> = {
  take_profit: { key: "tp_triggered", defaultValue: "TP Triggered" },
  stop_loss: { key: "sl_triggered", defaultValue: "SL Triggered" },
  liquidation: { key: "liquidated", defaultValue: "Liquidated" },
};

/**
 * Liquidation rows carry their own `position_effect`. When we also have an
 * exit reason to show, the effect collapses to the plain close action so the
 * cell reads "Close Long (Liquidated)" instead of "Liquidate Long (Liquidated)".
 */
const LIQUIDATION_EFFECT_BASE: Record<string, string> = {
  liquidate_long: "close_long",
  liquidate_short: "close_short",
};

/**
 * Narrows an untrusted backend string to a known exit reason. Takes the whole
 * row because most call sites hold a codegen trade type that predates the
 * field (the generic keeps those assignable without a cast at every site).
 */
export const getTradeExitReason = <T extends object>(
  trade?: T & { exit_reason?: string | null }
): TradeExitReason | undefined => {
  const raw = trade?.exit_reason;
  return raw && Object.prototype.hasOwnProperty.call(EXIT_REASON_LABELS, raw)
    ? (raw as TradeExitReason)
    : undefined;
};

/** Translated parenthetical, e.g. "Liquidated". Undefined when there's none. */
export const getExitReasonLabel = (
  exitReason: string | undefined,
  t: TFunction
): string | undefined => {
  const reason = getTradeExitReason({ exit_reason: exitReason });
  if (!reason) return undefined;
  const { key, defaultValue } = EXIT_REASON_LABELS[reason];
  return t(key, { defaultValue });
};

/**
 * Splits a trade-history Type cell into its base action and (optional) reason,
 * e.g. `("liquidate_long", "liquidation")` -> `{ base: "close long", reason:
 * "Liquidated" }`. The base is left lowercase-with-spaces because every call
 * site renders it inside a `text-transform: capitalize` container.
 */
export const getPositionEffectParts = (
  positionEffect: string | undefined,
  exitReason: string | undefined,
  t: TFunction
): { base?: string; reason?: string } => {
  const reason = getExitReasonLabel(exitReason, t);
  if (!positionEffect) return { reason };
  const effect = reason
    ? LIQUIDATION_EFFECT_BASE[positionEffect] || positionEffect
    : positionEffect;
  return { base: effect.replace(/_/g, " "), reason };
};

const withExitReason = (
  text: string,
  exitReason: string | undefined,
  t: TFunction
) => {
  const reason = getExitReasonLabel(exitReason, t);
  return reason ? `${text} (${reason})` : text;
};

export const getTradeSide = (
  side: SideResponse,
  isClosing: boolean,
  t: TFunction,
  isSpot?: boolean,
  exitReason?: string
) => {
  // Spot trades are simple Buy/Sell, not Open/Close Long/Short
  // (and never carry an exit reason).
  if (isSpot) {
    return {
      type: side,
      text: side === SideResponse.Buy ? t("buy") : t("sell"),
    };
  }

  if (isClosing) {
    const type =
      side === SideResponse.Buy ? SideResponse.Sell : SideResponse.Buy;

    const text = side === SideResponse.Buy ? t("close_short") : t("close_long");
    return { type, text: withExitReason(text, exitReason, t) };
  }

  const type = side;
  const text = side === SideResponse.Buy ? t("open_long") : t("open_short");

  return { type, text: withExitReason(text, exitReason, t) };
};

/**
 * Trade ID has an embedded match index
 * Within a single trade, there can be multiple matches e.g. a single taker order is matched with multiple maker orders
 * The match index informs the ordering of the matches and hence informs us how to sort the trade entries
 * @param tradeId eg. "DwmDn5XnEyiqx5AB5CM4W8bgD137ASX4Lz1XWBWDYHn8" (base58) or "1119529-0x...-22-71" (hyphenated)
 * @returns the match index embedded in the trade id
 */
export const decodeMatchIndex = (tradeId: string) => {
  // Check if it's a hyphenated format (our backend format: "blockNumber-txHash-orderId-matchIndex")
  if (tradeId.includes('-')) {
    const parts = tradeId.split('-');
    const lastPart = parts[parts.length - 1];
    return parseInt(lastPart, 10) || 0;
  }

  // Original base58 format (legacy exchange format)
  try {
    const bytes = base58.decode(tradeId);
    const arr = bytes.slice(bytes.length - 4, bytes.length);
    const int = new TextDecoder("utf-8").decode(arr);
    return parseInt(int, 10);
  } catch {
    return 0;
  }
};

export const sortTrades = (
  a: GetTradeHistory200ResponseTradeHistoryInner,
  b: GetTradeHistory200ResponseTradeHistoryInner
) => {
  // Try to sort based on timestamp
  const diffTimestamp =
    Number(b?.created_timestamp || 0) - Number(a?.created_timestamp || 0);

  // If timestamp is the same, sort based on match index
  if (diffTimestamp === 0) {
    const diffMatchIndex =
      decodeMatchIndex(b.trade_id) - decodeMatchIndex(a.trade_id);

    // If match index is the same, check for is_closing flag
    // Prioritize is_closing flag = true
    if (diffMatchIndex === 0) {
      return Number(a?.is_closing || 0) - Number(b?.is_closing || 0);
    }
    return diffMatchIndex;
  }

  return diffTimestamp;
};

export type TradeHistoryPnLFields = GetTradeHistory200ResponseTradeHistoryInner & {
  avg_entry_price?: string;
  avg_price?: string;
  entry_price?: string;
  gross_pnl?: string;
  gross_realized_pnl?: string;
  net_pnl?: string;
  net_realized_pnl?: string;
  position_fee?: string;
  borrowing_fee?: string;
  funding_fee?: string;
  total_fees?: string;
};

const firstDefined = (values: Array<string | undefined>) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

export const getTradeGrossPnl = (trade?: TradeHistoryPnLFields) =>
  firstDefined([trade?.gross_realized_pnl, trade?.gross_pnl, trade?.pnl]);

export const getTradeEntryPrice = (trade?: TradeHistoryPnLFields) => {
  const explicitEntryPrice = firstDefined([
    trade?.avg_entry_price,
    trade?.avg_price,
    trade?.entry_price,
  ]);

  if (explicitEntryPrice && Number(explicitEntryPrice) !== 0) {
    return explicitEntryPrice;
  }

  const price = Number(trade?.price);
  const amount = Number(trade?.amount);
  const grossPnl = Number(getTradeGrossPnl(trade));

  if (
    trade?.is_closing &&
    Number.isFinite(price) &&
    Number.isFinite(amount) &&
    Number.isFinite(grossPnl) &&
    price > 0 &&
    amount > 0
  ) {
    const entryPrice =
      trade.side === SideResponse.Buy
        ? price + grossPnl / amount
        : price - grossPnl / amount;

    if (Number.isFinite(entryPrice) && entryPrice > 0) {
      return String(entryPrice);
    }
  }

  return explicitEntryPrice;
};

export const getTradeTotalFees = (trade?: TradeHistoryPnLFields) =>
  firstDefined([trade?.total_fees, trade?.fees]) || "0";

export const getTradePositionFee = (trade?: TradeHistoryPnLFields) =>
  firstDefined([trade?.position_fee]) || "0";

export const getTradeBorrowingFee = (trade?: TradeHistoryPnLFields) =>
  firstDefined([trade?.borrowing_fee]) || "0";

export const getTradeFundingFee = (trade?: TradeHistoryPnLFields) =>
  firstDefined([trade?.funding_fee]) || "0";

export const getTradeNetPnl = (trade?: TradeHistoryPnLFields) =>
  firstDefined([trade?.net_realized_pnl, trade?.net_pnl]);

export const hasNonZeroTradeValue = (value?: string) =>
  Boolean(value) && Number(value) !== 0;
